import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import { DeckType, NftsWithUserOwnedType } from '../../../../types/types';
import { useNavigate } from 'react-router-dom';
import { CardInspectModal, cardArtUrl, cardTitle, onCardArtError } from './CardInspectModal';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useSecureNetwork } from '../../../../utils/secureNetwork';
import { useWalletReady } from '../../../../utils/useWalletReady';
import { useShowSnackbar } from '../../../../utils/snackbar';
import { holdUntil, formatHoldUntil } from '../../../../utils/securityHold';
import { useUser } from '../../../../contexts/UserContext';
import { Transaction } from '@solana/web3.js';
import SecurityIcon from '@mui/icons-material/Security';
import { ArenaPager } from '../../../../components/ArenaPager';
import { useTwoFactorPrompt } from '../../../../components/TwoFactorPrompt/useTwoFactorPrompt';
import { PT } from '../../../../components/portalUi';
import './SecureNFTCards.css';
import { track, trackError } from '../../../../utils/track';

// Utility function to format idempotency key as user-friendly transaction ID
const formatTransactionId = (idempotencyKey: string): string => {
  try {
    // Split the idempotency key: prefix_userId_timestamp_uuid
    const parts = idempotencyKey.split('_');
    if (parts.length >= 4) {
      // Extract the UUID (last part) and timestamp
      const uuid = parts[parts.length - 1];
      const timestamp = parts[parts.length - 2];

      // Use first 8 characters of UUID and last 6 digits of timestamp
      const uuidPart = uuid.substring(0, 8).toUpperCase();
      const timestampPart = timestamp.slice(-6);

      return `TX-${uuidPart}-${timestampPart}`;
    }

    // Fallback: use a hash of the full key
    const hash = idempotencyKey.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);

    return `TX-${Math.abs(hash).toString(36).toUpperCase().substring(0, 12)}`;
  } catch (error) {
    // Ultimate fallback
    return `TX-${idempotencyKey.substring(0, 8).toUpperCase()}...`;
  }
};

/* Which step of a deposit or withdrawal an error came from. It decides what the
 * member is told: a refusal before anything was signed, a cancel in the wallet,
 * or a confirm that broke off after the transfer may already have been sent. */
type CustodyStage = 'prepare' | 'sign' | 'confirm';

/* What to tell the member when a deposit or withdrawal throws. The server's own
 * message is used as it is — it is written for members, and reaches here intact
 * since utils/securityUtils stopped dropping it — except where the member needs
 * to hear something the server cannot say. `released`: the withdrawal
 * reservation was handed back, so nothing was sent. */
const custodyErrorNotice = (
  error: any,
  kind: 'withdrawal' | 'deposit',
  stage: CustodyStage,
  released: boolean
): { text: string; severity: 'info' | 'warning' | 'error' } => {
  const text: string = error?.message || '';
  const status: number | undefined = error?.status;
  const Kind = kind === 'withdrawal' ? 'Withdrawal' : 'Deposit';

  if (stage === 'sign' && /reject|denied|cancel/i.test(text)) {
    return {
      text: `${Kind} cancelled in your wallet. Nothing was sent.`,
      severity: 'info'
    };
  }

  /* No answer from the server (connection lost, timeout, 5xx) after the signed
   * transfer was handed over: it may well be on its way. Calling that "failed"
   * is what sends members to support about cards that arrive a minute later. */
  if (
    stage === 'confirm' &&
    !released &&
    (status === undefined || (status >= 500 && status !== 503))
  ) {
    return {
      text:
        kind === 'withdrawal'
          ? "We couldn't confirm your withdrawal yet. If it went through, the card reaches your wallet within a few minutes; if not, it comes back to your collection by itself. Please check before trying again."
          : "We couldn't confirm your deposit yet. If it went through, the card appears in your collection within a few minutes. Please check before trying again.",
      severity: 'warning'
    };
  }

  const reason = text || `${Kind} failed. Please try again.`;
  return {
    text: released
      ? `${reason} Nothing was sent, and the card is back in your collection.`
      : reason,
    severity:
      status === 503 || status === 429 || /per day|limit/i.test(reason)
        ? 'warning'
        : 'error'
  };
};

// Collection-card rarity accent (mirrors the in-game .ccard palette).
const rarityHex = (r?: string): string =>
  (
    ({
      common: '#8b919c',
      rare: '#5b9bd5',
      epic: '#a06fff',
      legendary: '#ffcf40',
      relic: '#38e8ff'
    } as Record<string, string>)[String(r || '').toLowerCase()] || '#8b919c'
  );

// The card tile's own mono (SecureNFTCards.css).
const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

/* Deposit / Withdraw: one quiet pair at the foot of the card, side by side and
 * the same width, in the portal's cyan rather than an alert's green and red.
 * Greyed out, a button keeps the pointer (so its title — the reason — shows on
 * hover) but stays natively disabled, so a click never reaches the handler or
 * the data-track click capture. */
const custodyRowSx = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '6px',
  mt: 'auto',
  px: '10px',
  pb: '10px'
} as const;

const custodyBtnSx = {
  minWidth: 0,
  height: { xs: 40, sm: 36 },
  px: 0.5,
  borderRadius: '8px',
  border: '1px solid rgba(56,232,255,0.28)',
  backgroundColor: 'rgba(56,232,255,0.06)',
  color: PT.tealText,
  font: `800 10.5px/1 ${MONO}`,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  transition:
    'background-color 0.14s ease, border-color 0.14s ease, color 0.14s ease',
  // Only where there is a real pointer: a tap would leave a phone's button lit.
  '@media (hover: hover)': {
    '&:hover:not(.Mui-disabled)': {
      backgroundColor: PT.tealSoft,
      borderColor: PT.tealBorder,
      color: PT.teal
    }
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${PT.teal}`,
    outlineOffset: '2px'
  },
  '&.Mui-disabled': {
    pointerEvents: 'auto',
    cursor: 'not-allowed',
    opacity: 0.45,
    color: PT.textMuted,
    borderColor: PT.border,
    backgroundColor: 'rgba(255,255,255,0.03)'
  }
} as const;

type SecureNFTCardsProps = {
  filteredCards: NftsWithUserOwnedType[];
  isCreateEditDeckModeEnabled: boolean;
  createEditDeck: DeckType | null;
  maxSameCardInDeck: number;
  setCreateEditDeck: (value: DeckType | null) => void;
  currentPage: number;
  totalPages: number;
  handlePrevPage: () => void;
  handleNextPage: () => void;
  amountOfCardsCurrentlyInDeck: number;
  numberOfCardsNeeded: number;
  maxSameTokenCardInDeck: number;
  onWithdrawalComplete: (nftId: number, amount: number) => void;
  onDepositComplete: (nftId: number, amount: number) => void;
  // Re-read the member's cards from the server (after a transfer still confirming).
  onCollectionStale?: () => void;
};

export const SecureNFTCards = ({
  filteredCards,
  isCreateEditDeckModeEnabled,
  createEditDeck,
  maxSameCardInDeck,
  setCreateEditDeck,
  currentPage,
  totalPages,
  handlePrevPage,
  handleNextPage,
  amountOfCardsCurrentlyInDeck,
  numberOfCardsNeeded,
  maxSameTokenCardInDeck,
  onWithdrawalComplete,
  onDepositComplete,
  onCollectionStale
}: SecureNFTCardsProps) => {
  const { publicKey, signTransaction, wallet } = useWallet();
  const { setVisible: setWalletPickerVisible } = useWalletModal();
  // Connect-on-demand, as on the token page: tapping Deposit/Withdraw with no
  // usable wallet starts the connection, rather than the buttons being hidden
  // until a wallet happens to be connected.
  const { ensureReady, notReadyMessage } = useWalletReady();
  const {
    secureNftWithdraw,
    secureNftWithdrawConfirm,
    secureNftWithdrawCancel,
    secureNftDeposit,
    secureNftDepositConfirm,
    checkSecureServicesHealth,
    securePut
  } = useSecureNetwork();
  const { showSnackbar } = useShowSnackbar();
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  // Card the member tapped to inspect — opens the buy-on-marketplace modal.
  const [inspectCard, setInspectCard] = useState<NftsWithUserOwnedType | null>(
    null
  );
  const handleBuyCard = (c: NftsWithUserOwnedType) => {
    const name = c.nft.name;
    try {
      track('collection.buy_click', { card: name, rarity: c.nft.rarity });
    } catch (_) {
      /* analytics best-effort */
    }
    setInspectCard(null);
    navigate('/member-portal/marketplace', { state: { card: name } });
  };
  const { promptForTwoFactor, twoFactorModal } = useTwoFactorPrompt();

  const [sendingNft, setSendingNft] = useState(false);
  const [processingOperation, setProcessingOperation] = useState<
    'withdrawal' | 'deposit' | null
  >(null);
  const [servicesHealthy, setServicesHealthy] = useState<boolean | null>(null);
  /* The server's NFT_DEPOSITS_ENABLED / NFT_WITHDRAWALS_ENABLED /
   * NFT_DAILY_WITHDRAWAL_LIMIT, from GET /secure-nfts/health. They are the one
   * switch for the whole feature — no portal constant to flip and redeploy. */
  const [depositsEnabled, setDepositsEnabled] = useState(false);
  const [withdrawalsEnabled, setWithdrawalsEnabled] = useState(false);
  const [dailyWithdrawalLimit, setDailyWithdrawalLimit] = useState<
    number | null
  >(null);
  const [currentIdempotencyKey, setCurrentIdempotencyKey] = useState<
    string | null
  >(null);
  const [processingNftId, setProcessingNftId] = useState<number | null>(null);
  /* Today's withdrawals are used up. The page keeps no count of its own: this
   * is set only when the server refuses a withdrawal for the daily cap (see the
   * catch in handleSecureNftWithdrawal), and lasts until the page reloads. */
  const [withdrawalLimitReached, setWithdrawalLimitReached] = useState(false);

  const isAdmin = user?.role === 'admin';
  // Admins keep both while a flag is off, to test before members get them.
  const depositsOpen = depositsEnabled || isAdmin;
  const withdrawalsOpen = withdrawalsEnabled || isAdmin;
  const showCustodyControls =
    !isCreateEditDeckModeEnabled &&
    servicesHealthy === true &&
    (depositsOpen || withdrawalsOpen);
  const offForMembers = [
    !depositsEnabled && 'deposits',
    !withdrawalsEnabled && 'withdrawals'
  ]
    .filter(Boolean)
    .join(' and ');
  const custodyBanner = [
    depositsOpen && withdrawalsOpen
      ? 'Deposit and withdraw Digital Collectibles with your Solflare wallet.'
      : withdrawalsOpen
        ? 'Withdraw Digital Collectibles to your Solflare wallet.'
        : 'Deposit Digital Collectibles from your Solflare wallet.',
    withdrawalsOpen && dailyWithdrawalLimit
      ? `You can withdraw up to ${dailyWithdrawalLimit} a day; the limit resets at midnight (Melbourne time).`
      : '',
    isAdmin && offForMembers
      ? `Admin preview: ${offForMembers} are still off for members.`
      : ''
  ]
    .filter(Boolean)
    .join(' ');

  // Check services health on component mount
  React.useEffect(() => {
    const checkHealth = async () => {
      try {
        const health = await checkSecureServicesHealth();
        const nftService = health.nftService || {};
        setServicesHealthy(nftService.status === 'healthy');
        setDepositsEnabled(nftService.depositsEnabled === true);
        setWithdrawalsEnabled(nftService.withdrawalsEnabled === true);
        setDailyWithdrawalLimit(
          Number.isInteger(nftService.dailyWithdrawalLimit)
            ? nftService.dailyWithdrawalLimit
            : null
        );
      } catch (error) {
        setServicesHealthy(false);
      }
    };

    checkHealth();
  }, [checkSecureServicesHealth]);

  /* After a transfer that is still confirming, re-read the collection a few
   * times so the count catches up by itself once the server finishes it (its
   * reconciler runs every minute). */
  const refreshTimers = React.useRef<number[]>([]);
  React.useEffect(() => {
    const timers = refreshTimers.current;
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);
  const scheduleCollectionRefresh = () => {
    if (!onCollectionStale) return;
    for (const seconds of [45, 120, 300]) {
      refreshTimers.current.push(
        window.setTimeout(onCollectionStale, seconds * 1000)
      );
    }
  };

  /* True (after telling the member why) when the wallet cannot be used yet.
   * Solflare only, by decision: the custody co-signing path is only proven with
   * Solflare, so another selected wallet opens the picker to switch. */
  const walletBlocked = (): boolean => {
    if (wallet && wallet.adapter.name !== 'Solflare') {
      trackError('wallet.nft', 'wallet_not_solflare');
      showSnackbar(
        'Moving Digital Collectibles needs the Solflare wallet. Choose Solflare, then tap again.',
        'warning'
      );
      setWalletPickerVisible(true);
      return true;
    }
    if (!ensureReady()) {
      trackError('wallet.nft', 'wallet_not_connected');
      showSnackbar(notReadyMessage, 'warning');
      return true;
    }
    return false;
  };

  const addCardToDeck = (card: NftsWithUserOwnedType) => {
    if (createEditDeck !== null && createEditDeck !== undefined) {
      const findCard = createEditDeck.deck.filter((cardInDeck) => {
        return cardInDeck.card.id === card.nft.id;
      });

      if (findCard.length > 0) {
        const newDeckArray = [...createEditDeck.deck];
        const cardIndex = newDeckArray.findIndex(
          (cardInDeck) => cardInDeck.card.id === card.nft.id
        );

        newDeckArray[cardIndex].amount = newDeckArray[cardIndex].amount + 1;
        setCreateEditDeck({ ...createEditDeck, deck: newDeckArray });
      } else if (findCard.length === 0) {
        const newDeckArray = [...createEditDeck.deck];
        newDeckArray.push({ card: card.nft, amount: 1 });
        setCreateEditDeck({ ...createEditDeck, deck: newDeckArray });
      }
    }
  };

  const handleSecureNftWithdrawal = async (nftId: number) => {
    let withdrawalIdempotencyKey: string | null = null;
    let stage: CustodyStage = 'prepare';
    if (walletBlocked()) return;
    // Redundant after walletBlocked() — kept so TS narrows publicKey/signTransaction.
    if (!publicKey || !signTransaction) return;

    if (servicesHealthy === false) {
      showSnackbar(
        'Digital Collectible services are currently unavailable. Please try again later.',
        'error'
      );
      return;
    }

    // Check if user owns the NFT
    const nftCard = filteredCards.find((card) => card.nft.id === nftId);
    if (!nftCard || nftCard.owned <= 0) {
      showSnackbar(
        "🎴 You don't own this Digital Collectible or have insufficient quantity to withdraw.",
        'error'
      );
      return;
    }

    // 48 hours after a 2FA reset nothing leaves: say so before asking for a code
    const held = holdUntil(user);
    if (held) {
      showSnackbar(`Withdrawals are paused until ${formatHoldUntil(held)} after your 2FA reset.`, 'warning');
      return;
    }

    // Blockchain operations require a 2FA code (verified server-side).
    const twoFactorToken = await promptForTwoFactor();
    if (!twoFactorToken) return;

    setSendingNft(true);
    setProcessingOperation('withdrawal');
    setProcessingNftId(nftId);

    try {
      // First, ensure the wallet address is up to date in the database
      const currentWalletAddress = publicKey.toString();

      if (user?.walletAddress !== currentWalletAddress) {
        console.log('⚠️ NFT wallet address mismatch detected, updating...');
        try {
          const result = await securePut(
            '/user/wallet-address',
            { walletAddress: currentWalletAddress },
            'WALLET_ADDRESS_UPDATE',
            true
          );

          console.log('✅ NFT wallet update result:', result);

          if (result.updated) {
            setUser({
              ...user,
              walletAddress: result.walletAddress || currentWalletAddress
            });
          }
        } catch (walletUpdateError: any) {
          console.error(
            '❌ Secure NFT wallet update error:',
            walletUpdateError
          );
          showSnackbar(
            walletUpdateError.message ||
              'Failed to update wallet address securely',
            'error'
          );
          return;
        }
      }

      // Step 1: Create the NFT withdrawal transaction
      console.log(`📝 Creating NFT withdrawal for NFT ID: ${nftId}`);

      const createResponse = await secureNftWithdraw(nftId, 1, twoFactorToken);
      console.log('🔒 NFT withdrawal response:', createResponse);

      const { transaction, idempotencyKey, nftName, nftType } = createResponse;
      withdrawalIdempotencyKey = idempotencyKey;
      setCurrentIdempotencyKey(idempotencyKey);

      if (!transaction || !idempotencyKey) {
        throw new Error('Invalid response from secure Digital Collectible service');
      }

      console.log('📦 NFT transaction received:', {
        nftId,
        nftName,
        nftType,
        idempotencyKey
      });

      // Step 2: Deserialize and validate transaction
      const transactionBuffer = Buffer.from(transaction, 'base64');
      const deserializedTx = Transaction.from(transactionBuffer);

      console.log('✅ NFT transaction deserialized successfully');
      console.log('📋 PRE-SIGNING NFT transaction details:', {
        feePayer: deserializedTx.feePayer?.toString(),
        instructionCount: deserializedTx.instructions.length,
        signatures: deserializedTx.signatures.length,
        recentBlockhash: deserializedTx.recentBlockhash,
        idempotencyKey,
        nftId
      });

      // Enhanced security validation
      if (deserializedTx.feePayer?.toString() !== currentWalletAddress) {
        throw new Error(
          'Digital Collectible transaction was created for a different wallet. Please try again.'
        );
      }

      // Step 3: Sign transaction
      console.log('🔏 Starting NFT transaction signing...');
      stage = 'sign';
      const signedTx = await signTransaction(deserializedTx);
      console.log('✅ NFT transaction signed successfully');

      const signedTxBase64 = signedTx
        .serialize({
          requireAllSignatures: false,
          verifySignatures: false
        })
        .toString('base64');

      console.log(
        '📤 NFT transaction serialized, confirming with blockchain...'
      );

      // Step 4: Confirm NFT withdrawal with blockchain-first approach
      stage = 'confirm';
      const confirmResponse = await secureNftWithdrawConfirm(
        signedTxBase64,
        idempotencyKey
      );

      console.log('🎉 NFT withdrawal confirmed:', confirmResponse);

      /* The card left the collection when the withdrawal was reserved, so the
       * count drops either way. `pending` (202) = sent, still confirming on-chain;
       * the server finishes it, or refunds it if it never lands. */
      const amountWithdrawn = Number(confirmResponse.amountWithdrawn || 1);
      onWithdrawalComplete(nftId, amountWithdrawn);
      if (confirmResponse.pending) {
        showSnackbar(
          confirmResponse.message ||
            'Your withdrawal has been sent and is still confirming. Check your wallet in a few minutes.',
          'info'
        );
        scheduleCollectionRefresh();
      } else {
        showSnackbar(
          `🎉 ${confirmResponse.message} Your collection has been updated.`,
          'success'
        );
      }
      // CRM: a card was withdrawn to the user's wallet (`pending`: sent, still
      // confirming). Fires only when the feature is enabled. See utils/track.ts.
      track('collection.withdraw', {
        nftId,
        amount: amountWithdrawn,
        pending: Boolean(confirmResponse.pending)
      });
    } catch (error: any) {
      trackError('wallet.nft', error);
      /* Hand the reservation back. The server releases only a withdrawal that
       * was never signed; one already signed or sent answers 409, and the server
       * finishes it or refunds it. */
      let released = false;
      if (withdrawalIdempotencyKey) {
        try {
          await secureNftWithdrawCancel(withdrawalIdempotencyKey);
          released = true;
        } catch {
          // Signed or sent — see above.
        }
      }

      console.error(`🚨 NFT withdrawal error details:`, {
        message: error.message,
        status: error.status,
        data: error.data,
        stage,
        nftId
      });

      /* The server's view may no longer match the page: a released reservation
       * puts the card back, one still on its way has already been taken. Re-read
       * rather than guess, and keep re-reading while it is unresolved. */
      onCollectionStale?.();
      if (!released) scheduleCollectionRefresh();

      /* Refused for the daily cap: the server's "…per day…" refusal, which
       * custodyErrorNotice already words as a warning. The per-minute rate
       * limit and the new-account cap do not say "per day". */
      if (/per day/i.test(error?.message || '')) {
        setWithdrawalLimitReached(true);
      }

      const notice = custodyErrorNotice(error, 'withdrawal', stage, released);
      showSnackbar(notice.text, notice.severity);
    } finally {
      setSendingNft(false);
      setProcessingOperation(null);
      setCurrentIdempotencyKey(null);
      setProcessingNftId(null);
    }
  };

  const handleSecureNftDeposit = async (nftId: number) => {
    let stage: CustodyStage = 'prepare';
    if (walletBlocked()) return;
    // Redundant after walletBlocked() — kept so TS narrows publicKey/signTransaction.
    if (!publicKey || !signTransaction) return;

    if (servicesHealthy === false) {
      showSnackbar('Digital Collectible services are currently unavailable.', 'error');
      return;
    }

    // Blockchain operations require a 2FA code (verified server-side).
    const twoFactorToken = await promptForTwoFactor();
    if (!twoFactorToken) return;

    setSendingNft(true);
    setProcessingOperation('deposit');
    setProcessingNftId(nftId);

    try {
      const currentWalletAddress = publicKey.toString();
      if (user?.walletAddress !== currentWalletAddress) {
        const result = await securePut(
          '/user/wallet-address',
          { walletAddress: currentWalletAddress },
          'WALLET_ADDRESS_UPDATE',
          true
        );

        if (result.updated) {
          setUser((currentUser) => ({
            ...currentUser,
            walletAddress: result.walletAddress || currentWalletAddress
          }));
        }
      }

      const createResponse = await secureNftDeposit(nftId, 1, twoFactorToken);
      const { transaction, idempotencyKey } = createResponse;
      if (!transaction || !idempotencyKey) {
        throw new Error('Invalid response from secure Digital Collectible deposit service.');
      }

      setCurrentIdempotencyKey(idempotencyKey);
      const depositTransaction = Transaction.from(
        Buffer.from(transaction, 'base64')
      );

      if (depositTransaction.feePayer?.toBase58() !== currentWalletAddress) {
        throw new Error('Digital Collectible deposit was created for a different wallet.');
      }

      stage = 'sign';
      const signedTransaction = await signTransaction(depositTransaction);
      const signedTransactionBase64 = signedTransaction
        .serialize({ requireAllSignatures: false, verifySignatures: false })
        .toString('base64');
      stage = 'confirm';
      const confirmResponse = await secureNftDepositConfirm(
        signedTransactionBase64,
        idempotencyKey
      );

      if (confirmResponse.pending) {
        /* Sent, not confirmed yet (202). The server credits the card when it
         * lands, so the count is re-read from the server rather than bumped
         * here — bumping it now would show a card that might never arrive. */
        showSnackbar(
          confirmResponse.message ||
            'Your deposit has been sent and is still confirming. It will appear in your collection within a few minutes.',
          'info'
        );
        scheduleCollectionRefresh();
        track('collection.deposit', { nftId, amount: 1, pending: true });
        return;
      }

      onDepositComplete(nftId, Number(confirmResponse.amountDeposited || 1));
      // CRM: a card was actually deposited into the portal (fires only when the
      // feature is enabled and a transfer confirms). See utils/track.ts.
      track('collection.deposit', {
        nftId,
        amount: Number(confirmResponse.amountDeposited || 1),
        pending: false
      });
      showSnackbar(confirmResponse.message, 'success');
    } catch (error: any) {
      trackError('wallet.nft', error);
      // A deposit that broke off at the confirm may still land and be credited.
      onCollectionStale?.();
      if (stage === 'confirm') scheduleCollectionRefresh();

      const notice = custodyErrorNotice(error, 'deposit', stage, false);
      showSnackbar(notice.text, notice.severity);
    } finally {
      setSendingNft(false);
      setProcessingOperation(null);
      setCurrentIdempotencyKey(null);
      setProcessingNftId(null);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {twoFactorModal}

      {/* Card inspector — big 3D spinning card + Buy on Marketplace (mirrors
          the arena deck-builder card viewer, minus cost / add-to-deck). */}
      <CardInspectModal
        card={inspectCard}
        onClose={() => setInspectCard(null)}
        onBuy={handleBuyCard}
      />

      {/* Current Operation Status */}
      {currentIdempotencyKey && processingNftId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Processing Digital Collectible {processingOperation || 'transfer'} (ID:{' '}
          {processingNftId}) - Transaction:{' '}
          {formatTransactionId(currentIdempotencyKey)}
        </Alert>
      )}

      {/* Deposit / withdraw, said once above the grid rather than on every card. */}
      {showCustodyControls && (
        <Alert
          severity="info"
          icon={<SecurityIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
        >
          {custodyBanner}
        </Alert>
      )}
      {isAdmin && !isCreateEditDeckModeEnabled && servicesHealthy === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The Digital Collectible service did not respond, so Deposit and
          Withdraw are hidden.
        </Alert>
      )}

      <ArenaPager
        page={currentPage}
        totalPages={totalPages}
        onPrev={handlePrevPage}
        onNext={handleNextPage}
      />

      <Grid container spacing={2}>
        {filteredCards.map((card, index) => {
          const checkIfCardInDeck = () => {
            if (createEditDeck !== null && createEditDeck !== undefined) {
              return createEditDeck.deck.filter((cardInDeck) => {
                return cardInDeck.card.id === card.nft.id;
              });
            } else {
              return [];
            }
          };

          const checkIfMaxCardQuantityInDeck = () => {
            if (checkIfCardInDeck().length > 0) {
              return checkIfCardInDeck()[0].amount;
            } else {
              return 0;
            }
          };

          const cardOwnedIsMoreThanOrEqualToQuantityInDeck = () => {
            if (checkIfCardInDeck().length > 0) {
              return checkIfCardInDeck()[0].amount + 1 <= card.owned;
            } else {
              return true;
            }
          };

          const checkIfCardOfTheSameName = () => {
            if (createEditDeck !== null && createEditDeck !== undefined) {
              return createEditDeck.deck.filter((cardInDeck) => {
                return cardInDeck.card.name === card.nft.name;
              });
            } else {
              return [];
            }
          };

          const allCardsOwnedIsMoreThanMax = () => {
            if (checkIfCardOfTheSameName().length > 0) {
              let counter = 0;
              checkIfCardOfTheSameName().forEach((card) => {
                counter = counter + card.amount;
              });
              return counter;
            } else {
              return 0;
            }
          };

          // Copies are capped per card NAME across all rarities, matching the
          // game (cardCost.js / arena): 3 for normal cards, 8 for tokens. The
          // per-name tally is allCardsOwnedIsMoreThanMax().
          const nameLimit =
            card.nft.type === 'token'
              ? maxSameTokenCardInDeck
              : maxSameCardInDeck;

          const isCurrentlyProcessing = processingNftId === card.nft.id;

          /* Why this card's Deposit / Withdraw is greyed out ('' = it is not),
           * shown as the button's title. */
          const transferInFlight = sendingNft || isCurrentlyProcessing;
          const depositBlocked = !depositsOpen
            ? 'Deposits are switched off'
            : transferInFlight
              ? 'Another transfer is in progress'
              : '';
          const withdrawBlocked = !withdrawalsOpen
            ? 'Withdrawals are switched off'
            : card.owned === 0
              ? "You don't own this card"
              : withdrawalLimitReached
                ? 'Daily withdrawal limit reached'
                : transferInFlight
                  ? 'Another transfer is in progress'
                  : '';

          return (
            // Five a row at lg (2.4 of 12 = 20%), so the pair fits side by side.
            <Grid item xs={6} md={4} lg={2.4} key={index}>
              <div
                className={`dcoll-card${
                  !isCreateEditDeckModeEnabled && card.owned <= 0
                    ? ' unowned'
                    : ''
                }`}
              >
                {/* Processing Overlay */}
                {isCurrentlyProcessing && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                      borderRadius: '8px'
                    }}
                  >
                    <Box textAlign="center">
                      <CircularProgress size={40} color="primary" />
                      <Typography
                        variant="caption"
                        display="block"
                        mt={1}
                        color="white"
                      >
                        Secure Processing...
                      </Typography>
                    </Box>
                  </Box>
                )}

                <div
                  className="art"
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setInspectCard(card)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setInspectCard(card);
                    }
                  }}
                >
                  <img
                    src={cardArtUrl(card.nft)}
                    alt={card.nft.name}
                    loading="lazy"
                    onError={onCardArtError}
                  />
                  <span
                    className="rr"
                    style={{ color: rarityHex(card.nft.rarity) }}
                  >
                    {card.nft.rarity}
                  </span>
                  {(card.locked || 0) > 0 && (
                    <span
                      className="lock"
                      title={`${card.locked} ${card.locked === 1 ? 'copy' : 'copies'} from your free starter pack — locked until you verify your identity or spend A$99`}
                    >
                      LOCKED{(card.locked || 0) < card.owned ? ` ×${card.locked}` : ''}
                    </span>
                  )}
                  {card.owned > 0 && (
                    <span className="qty">×{card.owned}</span>
                  )}
                </div>

                <div className="info">
                  <div
                    className="rar"
                    style={{ color: rarityHex(card.nft.rarity) }}
                  >
                    {card.nft.rarity}
                  </div>
                  <div className="nm" title={cardTitle(card.nft.name)}>
                    {cardTitle(card.nft.name)}
                  </div>
                  {card.nft.faction && (
                    <div className="fac">{card.nft.faction}</div>
                  )}
                  {(card.nft.type === 'character' ||
                    card.nft.type === 'influencer') && (
                    <div className="stats">
                      <span>
                        ATK <b>{card.nft.attack}</b>
                      </span>
                      <span>
                        DEF <b>{card.nft.defence}</b>
                      </span>
                    </div>
                  )}

                  {isCreateEditDeckModeEnabled && (
                    <div className="dcoll-indeck">
                      In deck: {checkIfMaxCardQuantityInDeck()}
                    </div>
                  )}
                </div>

                {/* Deck Management Buttons */}
                {isCreateEditDeckModeEnabled && (
                  <>
                    {allCardsOwnedIsMoreThanMax() < nameLimit &&
                      cardOwnedIsMoreThanOrEqualToQuantityInDeck() &&
                      amountOfCardsCurrentlyInDeck < numberOfCardsNeeded && (
                        <Button
                          variant="contained"
                          onClick={() => addCardToDeck(card)}
                          fullWidth
                          size="large"
                          sx={{ mt: 1 }}
                          disabled={isCurrentlyProcessing}
                        >
                          Add
                        </Button>
                      )}

                    {allCardsOwnedIsMoreThanMax() < nameLimit &&
                      (!cardOwnedIsMoreThanOrEqualToQuantityInDeck() ||
                        amountOfCardsCurrentlyInDeck >=
                          numberOfCardsNeeded) && (
                        <Button
                          variant="contained"
                          disabled
                          fullWidth
                          size="large"
                          sx={{ mt: 1 }}
                        >
                          Add
                        </Button>
                      )}

                    {allCardsOwnedIsMoreThanMax() >= nameLimit && (
                      <Button
                        variant="contained"
                        disabled
                        fullWidth
                        size="large"
                        sx={{ mt: 1 }}
                      >
                        Max Same Card
                      </Button>
                    )}
                  </>
                )}

                {/* Deposit / withdraw — shown when the server has either switched
                    on (admins also while off, to test). Both are always there,
                    each greyed out on its own with the reason as its title, so
                    every tile keeps the same layout. The wallet is connected on
                    tap, Solflare only. */}
                {showCustodyControls && (
                  <Box sx={custodyRowSx}>
                    <ButtonBase
                      disabled={Boolean(depositBlocked)}
                      aria-disabled={Boolean(depositBlocked)}
                      title={depositBlocked || undefined}
                      onClick={() => handleSecureNftDeposit(card.nft.id)}
                      data-track="collection.deposit_click"
                      data-track-nft={String(card.nft.id)}
                      sx={custodyBtnSx}
                    >
                      {isCurrentlyProcessing &&
                      processingOperation === 'deposit' ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        'Deposit'
                      )}
                    </ButtonBase>

                    <ButtonBase
                      disabled={Boolean(withdrawBlocked)}
                      aria-disabled={Boolean(withdrawBlocked)}
                      title={withdrawBlocked || undefined}
                      onClick={() => handleSecureNftWithdrawal(card.nft.id)}
                      data-track="collection.withdraw_click"
                      data-track-nft={String(card.nft.id)}
                      sx={custodyBtnSx}
                    >
                      {isCurrentlyProcessing &&
                      processingOperation === 'withdrawal' ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        'Withdraw'
                      )}
                    </ButtonBase>
                  </Box>
                )}
              </div>
            </Grid>
          );
        })}
      </Grid>

      <ArenaPager
        page={currentPage}
        totalPages={totalPages}
        onPrev={handlePrevPage}
        onNext={handleNextPage}
      />
    </Box>
  );
};
